import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Coins,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  X
} from 'lucide-react';
import { FaInstagram, FaYoutube } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import currencyData from '../../data/currency.json';
import axios from '../../services/axiosConfig';
import LoadingSpinner from '../../components/LoadingSpinner';

const DESCRIPTION_LIMIT = 200;

const PLATFORM_CONFIG = {
  youtube: {
    label: 'YouTube',
    subtitle: 'Create polished offer cards for Shorts, long videos, live streams, and collaborations.',
    icon: FaYoutube,
    iconClass: 'bg-red-100 text-red-600',
    accentClass: 'border-gray-200 bg-white',
    services: [
      {
        key: 'shortVideo',
        label: 'Short Video',
        hint: 'Quick video placements, Shorts, or fast campaign content.'
      },
      {
        key: 'longVideo',
        label: 'Long Video',
        hint: 'Dedicated brand integrations or longer-form campaign videos.'
      },
      {
        key: 'liveStream',
        label: 'Live Stream',
        hint: 'Live launches, premieres, and real-time audience activations.'
      },
      {
        key: 'collaboration',
        label: 'Collaboration',
        hint: 'Bundles, series partnerships, or custom creator campaigns.'
      }
    ]
  },
  instagram: {
    label: 'Instagram',
    subtitle: 'Manage feed, story, reel, and collaboration cards with clear pricing and short descriptions.',
    icon: FaInstagram,
    iconClass: 'bg-pink-100 text-pink-600',
    accentClass: 'border-gray-200 bg-white',
    services: [
      {
        key: 'post',
        label: 'Post',
        hint: 'Single image, carousel, or branded feed placement.'
      },
      {
        key: 'story',
        label: 'Story',
        hint: 'Short-lived story content with strong direct response value.'
      },
      {
        key: 'reel',
        label: 'Reel',
        hint: 'Short-form vertical video built for reach and discovery.'
      },
      {
        key: 'collaboration',
        label: 'Collaboration',
        hint: 'Retainers, bundles, or custom partnership campaigns.'
      }
    ]
  }
};

const createInitialPricingData = () => ({
  youtube: {
    currency: null,
    pricing: {
      shortVideo: '',
      longVideo: '',
      liveStream: '',
      collaboration: ''
    },
    descriptions: {
      shortVideo: '',
      longVideo: '',
      liveStream: '',
      collaboration: ''
    }
  },
  instagram: {
    currency: null,
    pricing: {
      post: '',
      story: '',
      reel: '',
      collaboration: ''
    },
    descriptions: {
      post: '',
      story: '',
      reel: '',
      collaboration: ''
    }
  }
});

const clonePricingData = (data = createInitialPricingData()) => ({
  youtube: {
    currency: data.youtube?.currency || null,
    pricing: {
      shortVideo: data.youtube?.pricing?.shortVideo ?? '',
      longVideo: data.youtube?.pricing?.longVideo ?? '',
      liveStream: data.youtube?.pricing?.liveStream ?? '',
      collaboration: data.youtube?.pricing?.collaboration ?? ''
    },
    descriptions: {
      shortVideo: data.youtube?.descriptions?.shortVideo ?? '',
      longVideo: data.youtube?.descriptions?.longVideo ?? '',
      liveStream: data.youtube?.descriptions?.liveStream ?? '',
      collaboration: data.youtube?.descriptions?.collaboration ?? ''
    }
  },
  instagram: {
    currency: data.instagram?.currency || null,
    pricing: {
      post: data.instagram?.pricing?.post ?? '',
      story: data.instagram?.pricing?.story ?? '',
      reel: data.instagram?.pricing?.reel ?? '',
      collaboration: data.instagram?.pricing?.collaboration ?? ''
    },
    descriptions: {
      post: data.instagram?.descriptions?.post ?? '',
      story: data.instagram?.descriptions?.story ?? '',
      reel: data.instagram?.descriptions?.reel ?? '',
      collaboration: data.instagram?.descriptions?.collaboration ?? ''
    }
  }
});

const normalizePricingData = (data) => ({
  youtube: {
    currency: data?.youtube?.currency || null,
    pricing: {
      shortVideo:
        data?.youtube?.pricing?.shortVideo > 0 ? String(data.youtube.pricing.shortVideo) : '',
      longVideo:
        data?.youtube?.pricing?.longVideo > 0 ? String(data.youtube.pricing.longVideo) : '',
      liveStream:
        data?.youtube?.pricing?.liveStream > 0 ? String(data.youtube.pricing.liveStream) : '',
      collaboration:
        data?.youtube?.pricing?.collaboration > 0
          ? String(data.youtube.pricing.collaboration)
          : ''
    },
    descriptions: {
      shortVideo: String(data?.youtube?.descriptions?.shortVideo || ''),
      longVideo: String(data?.youtube?.descriptions?.longVideo || ''),
      liveStream: String(data?.youtube?.descriptions?.liveStream || ''),
      collaboration: String(data?.youtube?.descriptions?.collaboration || '')
    }
  },
  instagram: {
    currency: data?.instagram?.currency || null,
    pricing: {
      post: data?.instagram?.pricing?.post > 0 ? String(data.instagram.pricing.post) : '',
      story: data?.instagram?.pricing?.story > 0 ? String(data.instagram.pricing.story) : '',
      reel: data?.instagram?.pricing?.reel > 0 ? String(data.instagram.pricing.reel) : '',
      collaboration:
        data?.instagram?.pricing?.collaboration > 0
          ? String(data.instagram.pricing.collaboration)
          : ''
    },
    descriptions: {
      post: String(data?.instagram?.descriptions?.post || ''),
      story: String(data?.instagram?.descriptions?.story || ''),
      reel: String(data?.instagram?.descriptions?.reel || ''),
      collaboration: String(data?.instagram?.descriptions?.collaboration || '')
    }
  }
});

const parsePrice = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
};

const formatAmount = (value) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  }).format(value);

const countConfiguredServices = (platformData) =>
  Object.values(platformData?.pricing || {}).filter((value) => parsePrice(value) > 0).length;

const getMinimumPrice = (platformData) => {
  const prices = Object.values(platformData?.pricing || {})
    .map(parsePrice)
    .filter((value) => value > 0);

  return prices.length > 0 ? Math.min(...prices) : null;
};

const validCurrencies = (currencyData?.countries || []).filter(
  (country) => country?.name && country?.currency && country?.code
);

const currencyLookup = new Map(validCurrencies.map((country) => [country.code, country]));

const ServicePayment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pricingData, setPricingData] = useState(createInitialPricingData);
  const [savedPricingData, setSavedPricingData] = useState(createInitialPricingData);
  const [currencyEditorPlatform, setCurrencyEditorPlatform] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [searchCountry, setSearchCountry] = useState('');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [savingKey, setSavingKey] = useState('');

  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/auth/users/service-payment', {
          withCredentials: true
        });

        if (response.data?.success) {
          const normalized = normalizePricingData(response.data.data);
          setPricingData(normalized);
          setSavedPricingData(normalized);
        }
      } catch (error) {
        console.error('Error fetching pricing data:', error);
        toast.error('Failed to load pricing data.');
      } finally {
        setLoading(false);
      }
    };

    fetchPricingData();
  }, []);

  useEffect(() => {
    if (!showCurrencyDropdown) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (!event.target.closest('.currency-dropdown-container')) {
        setShowCurrencyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCurrencyDropdown]);

  const filteredCurrencies = useMemo(() => {
    const query = searchCountry.trim().toLowerCase();

    if (!query) {
      return validCurrencies.slice(0, 24);
    }

    return validCurrencies
      .filter((country) =>
        [country.name, country.currency, country.code]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(query))
      )
      .slice(0, 24);
  }, [searchCountry]);

  const overviewStats = useMemo(() => {
    const platforms = Object.keys(PLATFORM_CONFIG);
    return {
      livePlatforms: platforms.filter((platform) => countConfiguredServices(pricingData[platform]) > 0)
        .length,
      totalServices: platforms.reduce(
        (count, platform) => count + countConfiguredServices(pricingData[platform]),
        0
      )
    };
  }, [pricingData]);

  const savePlatform = async (platform, nextState, successMessage) => {
    const platformData = nextState[platform];
    const payload = {
      platform,
      currency: platformData.currency || null,
      pricing: Object.fromEntries(
        Object.entries(platformData.pricing).map(([serviceKey, value]) => [serviceKey, parsePrice(value)])
      ),
      descriptions: Object.fromEntries(
        Object.entries(platformData.descriptions).map(([serviceKey, value]) => [
          serviceKey,
          String(value || '').trim().slice(0, DESCRIPTION_LIMIT)
        ])
      )
    };

    try {
      setSavingKey(platform);
      const response = await axios.put('/auth/users/service-payment', payload, {
        withCredentials: true
      });

      if (response.data?.success) {
        const normalized = normalizePricingData(response.data.data);
        setPricingData(normalized);
        setSavedPricingData(normalized);
        setCurrencyEditorPlatform(null);
        setEditingCard(null);
        setSearchCountry('');
        setShowCurrencyDropdown(false);
        toast.success(successMessage || response.data.message || 'Pricing updated successfully.');
        return true;
      }
    } catch (error) {
      console.error('Error saving pricing data:', error);
      toast.error('Failed to save pricing data.');
    } finally {
      setSavingKey('');
    }

    return false;
  };

  const openCurrencyEditor = (platform) => {
    const nextState = clonePricingData(savedPricingData);
    const existingCurrency = nextState[platform]?.currency
      ? currencyLookup.get(nextState[platform].currency)
      : null;

    setPricingData(nextState);
    setCurrencyEditorPlatform(platform);
    setEditingCard(null);
    setSearchCountry(existingCurrency ? `${existingCurrency.name} (${existingCurrency.currency})` : '');
    setShowCurrencyDropdown(false);
  };

  const cancelCurrencyEdit = () => {
    setPricingData(clonePricingData(savedPricingData));
    setCurrencyEditorPlatform(null);
    setSearchCountry('');
    setShowCurrencyDropdown(false);
  };

  const handleCurrencySelect = (platform, country) => {
    setPricingData((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        currency: country.code
      }
    }));
    setSearchCountry(`${country.name} (${country.currency})`);
    setShowCurrencyDropdown(false);
  };

  const handleCurrencySave = async (platform) => {
    await savePlatform(platform, pricingData, `${PLATFORM_CONFIG[platform].label} currency updated.`);
  };

  const handleCurrencyClear = async (platform) => {
    if (countConfiguredServices(savedPricingData[platform]) > 0) {
      toast.error('Delete all pricing cards for this platform before clearing the currency.');
      return;
    }

    const nextState = clonePricingData(savedPricingData);
    nextState[platform].currency = null;
    setPricingData(nextState);
    await savePlatform(platform, nextState, `${PLATFORM_CONFIG[platform].label} currency removed.`);
  };

  const openCardEditor = (platform, serviceKey) => {
    setPricingData(clonePricingData(savedPricingData));
    setCurrencyEditorPlatform(null);
    setEditingCard({ platform, serviceKey });
    setSearchCountry('');
    setShowCurrencyDropdown(false);
  };

  const cancelCardEditor = () => {
    setPricingData(clonePricingData(savedPricingData));
    setEditingCard(null);
  };

  const handlePriceChange = (platform, serviceKey, value) => {
    if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) {
      return;
    }

    setPricingData((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        pricing: {
          ...prev[platform].pricing,
          [serviceKey]: value
        }
      }
    }));
  };

  const handleDescriptionChange = (platform, serviceKey, value) => {
    setPricingData((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        descriptions: {
          ...prev[platform].descriptions,
          [serviceKey]: value.slice(0, DESCRIPTION_LIMIT)
        }
      }
    }));
  };

  const handleSaveCard = async (platform, serviceKey) => {
    const platformData = pricingData[platform];
    const price = parsePrice(platformData.pricing[serviceKey]);
    const description = String(platformData.descriptions[serviceKey] || '').trim();

    if (!platformData.currency) {
      toast.error('Please select a currency for this platform first.');
      return;
    }

    if (price <= 0) {
      toast.error('Please add a valid price before saving this card.');
      return;
    }

    if (!description) {
      toast.error('Please add a short description for this pricing card.');
      return;
    }

    await savePlatform(platform, pricingData, `${PLATFORM_CONFIG[platform].label} card saved.`);
  };

  const handleDeleteCard = async (platform, serviceKey) => {
    const nextState = clonePricingData(savedPricingData);
    nextState[platform].pricing[serviceKey] = '';
    nextState[platform].descriptions[serviceKey] = '';
    setPricingData(nextState);
    await savePlatform(platform, nextState, `${PLATFORM_CONFIG[platform].label} card deleted.`);
  };

  if (loading) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center p-4 lg:p-6">
        <LoadingSpinner message="Loading your pricing cards..." />
      </div>
    );
  }

  return (
    <div className="w-full p-4 lg:p-6">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-orange-600 lg:mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>

      <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mb-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">Service Pricing</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage platform currencies and pricing cards for your marketplace profile.
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Marketplace sync enabled
          </span>
        </div>
      </section>

      <div className="mb-4 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:mb-6">
        <StatCard
          icon={<CircleDollarSign className="h-4 w-4" />}
          label="Live platforms"
          value={String(overviewStats.livePlatforms)}
          tone="orange"
        />
        <StatCard
          icon={<Coins className="h-4 w-4" />}
          label="Active cards"
          value={String(overviewStats.totalServices)}
          tone="orange"
        />
        <StatCard
          icon={<CheckCircle2 className="h-4 w-4" />}
          label="Description limit"
          value={`${DESCRIPTION_LIMIT} chars`}
          tone="emerald"
        />
      </div>

      <div className="mb-4 lg:mb-6">
        <h2 className="text-base font-bold text-gray-900 md:text-lg">Platform Pricing</h2>
        <p className="mt-1 text-sm text-gray-500">
          Use each platform panel to manage currency and individual pricing cards.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
            {Object.entries(PLATFORM_CONFIG).map(([platform, config]) => {
              const Icon = config.icon;
              const platformData = pricingData[platform];
              const savedPlatformData = savedPricingData[platform];
              const currency = platformData.currency ? currencyLookup.get(platformData.currency) : null;
              const configuredCount = countConfiguredServices(savedPlatformData);
              const minimumPrice = getMinimumPrice(savedPlatformData);
              const isCurrencyEditing = currencyEditorPlatform === platform;

              return (
                <article
                  key={platform}
                  className={`rounded-2xl border p-4 shadow-sm sm:p-5 ${config.accentClass}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className={`rounded-xl p-2.5 ${config.iconClass}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <h2 className="text-base font-bold text-gray-900 sm:text-lg">{config.label}</h2>
                        <p className="mt-1 text-xs text-gray-500 sm:text-sm">{config.subtitle}</p>
                      </div>
                    </div>

                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold sm:text-xs ${
                        configuredCount > 0
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {configuredCount > 0 ? 'Live' : 'Draft'}
                    </span>
                  </div>

                  <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-3 sm:p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 sm:text-xs">Currency</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900 sm:text-base">
                          {currency
                            ? `${currency.symbol} ${currency.currency} (${currency.code})`
                            : 'No currency selected'}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          Starting from {minimumPrice ? `${currency?.symbol || ''}${formatAmount(minimumPrice)}` : '--'}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => openCurrencyEditor(platform)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 transition hover:bg-orange-100 sm:text-sm"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          {currency ? 'Edit currency' : 'Set currency'}
                        </button>
                        {savedPlatformData.currency && configuredCount === 0 && (
                          <button
                            type="button"
                            onClick={() => handleCurrencyClear(platform)}
                            disabled={savingKey === platform}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-100 sm:text-sm"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    {isCurrencyEditing && (
                      <div className="currency-dropdown-container mt-3 rounded-xl border border-orange-100 bg-white p-3 sm:p-4">
                        <label className="mb-1.5 block text-xs font-medium text-gray-700 sm:text-sm">
                          Search and select currency
                        </label>
                        <div className="relative">
                          <Search
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                          />
                          <input
                            type="text"
                            value={searchCountry}
                            onChange={(event) => {
                              setSearchCountry(event.target.value);
                              setShowCurrencyDropdown(true);
                            }}
                            onFocus={() => setShowCurrencyDropdown(true)}
                            placeholder="Search by country, currency, or code"
                            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                          />
                          <ChevronDown
                            className={`absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition ${
                              showCurrencyDropdown ? 'rotate-180' : ''
                            }`}
                          />
                        </div>

                        {showCurrencyDropdown && (
                          <div className="mt-2 max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg">
                            {filteredCurrencies.length > 0 ? (
                              filteredCurrencies.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  onClick={() => handleCurrencySelect(platform, country)}
                                  className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition hover:bg-orange-50"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-sm font-semibold text-gray-700">
                                      {country.symbol}
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{country.name}</p>
                                      <p className="text-xs text-gray-500">
                                        {country.currency} ({country.code})
                                      </p>
                                    </div>
                                  </div>
                                  <span className="text-xs font-medium text-orange-600">Select</span>
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-6 text-center text-sm text-gray-500">
                                No matching currencies found.
                              </div>
                            )}
                          </div>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleCurrencySave(platform)}
                            disabled={savingKey === platform}
                            className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white transition sm:text-sm ${
                              savingKey === platform
                                ? 'cursor-not-allowed bg-gray-400'
                                : 'bg-orange-500 hover:bg-orange-600'
                            }`}
                          >
                            <Save className="h-3.5 w-3.5" />
                            {savingKey === platform ? 'Saving...' : 'Save currency'}
                          </button>
                          <button
                            type="button"
                            onClick={cancelCurrencyEdit}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 sm:text-sm"
                          >
                            <X className="h-3.5 w-3.5" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {config.services.map((service) => {
                      const savedPrice = parsePrice(savedPlatformData.pricing[service.key]);
                      const description = String(platformData.descriptions[service.key] || '');
                      const savedDescription = String(savedPlatformData.descriptions[service.key] || '');
                      const isEditingCard =
                        editingCard?.platform === platform && editingCard?.serviceKey === service.key;

                      if (isEditingCard) {
                        return (
                          <article
                            key={service.key}
                            className="rounded-2xl border border-orange-200 bg-white p-4 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="text-sm font-semibold text-gray-900 sm:text-base">{service.label}</h3>
                                <p className="mt-1 text-xs text-gray-500">{service.hint}</p>
                              </div>
                              <span className="rounded-full bg-orange-50 px-2.5 py-1 text-[10px] font-semibold text-orange-700 sm:text-xs">
                                Editing
                              </span>
                            </div>

                            <div className="mt-4 space-y-3">
                              <div>
                                <label className="mb-1.5 block text-xs font-medium text-gray-700 sm:text-sm">
                                  Price
                                </label>
                                <div className="relative">
                                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                                    {currency?.symbol || '$'}
                                  </span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    inputMode="decimal"
                                    onWheel={(event) => event.target.blur()}
                                    value={platformData.pricing[service.key]}
                                    onChange={(event) =>
                                      handlePriceChange(platform, service.key, event.target.value)
                                    }
                                    placeholder="0.00"
                                    className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-8 pr-3 text-sm text-gray-900 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                  />
                                </div>
                              </div>

                              <div>
                                <div className="mb-1.5 flex items-center justify-between gap-3">
                                  <label className="text-xs font-medium text-gray-700 sm:text-sm">
                                    Description
                                  </label>
                                  <span className="text-xs text-gray-400">
                                    {description.length}/{DESCRIPTION_LIMIT}
                                  </span>
                                </div>
                                <textarea
                                  rows={4}
                                  maxLength={DESCRIPTION_LIMIT}
                                  value={description}
                                  onChange={(event) =>
                                    handleDescriptionChange(platform, service.key, event.target.value)
                                  }
                                  placeholder="Describe what the brand gets in this service card."
                                  className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                />
                              </div>

                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleSaveCard(platform, service.key)}
                                  disabled={savingKey === platform}
                                  className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white transition sm:text-sm ${
                                    savingKey === platform
                                      ? 'cursor-not-allowed bg-gray-400'
                                      : 'bg-orange-500 hover:bg-orange-600'
                                  }`}
                                >
                                  <Save className="h-3.5 w-3.5" />
                                  {savingKey === platform ? 'Saving...' : 'Save card'}
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelCardEditor}
                                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 sm:text-sm"
                                >
                                  <X className="h-3.5 w-3.5" />
                                  Cancel
                                </button>
                                {savedPrice > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteCard(platform, service.key)}
                                    disabled={savingKey === platform}
                                    className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-xs font-medium text-red-600 transition hover:bg-red-100 sm:text-sm"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          </article>
                        );
                      }

                      if (savedPrice > 0) {
                        return (
                          <article
                            key={service.key}
                            className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="text-sm font-semibold text-gray-900 sm:text-base">{service.label}</h3>
                                <p className="mt-1 text-xs text-gray-500">{service.hint}</p>
                              </div>
                              <div className="rounded-xl bg-orange-50 px-2.5 py-1.5 text-right">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-orange-500">
                                  Price
                                </p>
                                <p className="mt-0.5 text-sm font-bold text-orange-700 sm:text-base">
                                  {currency?.symbol || ''}{formatAmount(savedPrice)}
                                </p>
                              </div>
                            </div>

                            <p className="mt-3 min-h-[56px] text-sm leading-relaxed text-gray-600">
                              {savedDescription || 'No description added yet.'}
                            </p>

                            <div className="mt-4 flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => openCardEditor(platform, service.key)}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 transition hover:bg-orange-100 sm:text-sm"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteCard(platform, service.key)}
                                disabled={savingKey === platform}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-100 sm:text-sm"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                              </button>
                            </div>
                          </article>
                        );
                      }

                      return (
                        <article
                          key={service.key}
                          className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4"
                        >
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">{service.label}</h3>
                            <p className="mt-1 text-xs text-gray-500">{service.hint}</p>
                          </div>

                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => openCardEditor(platform, service.key)}
                              className="inline-flex items-center gap-1.5 rounded-xl bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-orange-600 sm:text-sm"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add pricing card
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </article>
              );
            })}
          </section>
    </div>
  );
};

const StatCard = ({ icon, label, value, tone = 'orange' }) => (
  <div
    className={`min-w-0 rounded-xl border p-3 sm:p-4 ${
      tone === 'emerald' ? 'border-emerald-100 bg-emerald-50' : 'border-orange-100 bg-orange-50'
    }`}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <p className={`truncate text-xs font-medium sm:text-sm ${
          tone === 'emerald' ? 'text-emerald-700' : 'text-orange-700'
        }`}>
          {label}
        </p>
        <p className="mt-0.5 truncate text-sm font-bold text-gray-900 sm:text-base lg:text-lg">{value}</p>
      </div>
      <span className={`shrink-0 ${tone === 'emerald' ? 'text-emerald-600' : 'text-orange-600'}`}>
        {icon}
      </span>
    </div>
  </div>
);

export default ServicePayment;
